import type { MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Group, Stack, Text, ThemeIcon, UnstyledButton } from "@mantine/core";
import { BookOpen, ChevronRight } from "lucide-react";
import { GuideProgress } from "@nodus/ui";
import type { GuideSummary } from "@/lib/api/guides";
import { faqIcon } from "@/lib/faq-icons";
import { withGuide } from "@/lib/guide-stepper";

/**
 * The rows of the login popup: icon, title, summary and progress per guide.
 * Each row links to the current page with the guide open (`?guide=`); `onOpen` gets the click first (the
 * popup closes itself and opens the guide modal). Renders nothing without guides.
 */
export function GuidePopupList({
  guides,
  onOpen,
}: {
  guides: GuideSummary[];
  onOpen: (key: string, e: MouseEvent) => void;
}) {
  const { pathname, search } = useLocation();
  if (guides.length === 0) return null;
  return (
    <Stack gap="xs">
      {guides.map((g) => {
        const Icon = g.icon ? faqIcon(g.icon) : BookOpen;
        return (
          <UnstyledButton
            key={g.key}
            component={Link}
            to={{ pathname, search: withGuide(search, g.key) }}
            onClick={(e: MouseEvent) => onOpen(g.key, e)}
            aria-label={`${g.done > 0 ? "Continue" : "Start"}: ${g.title}`}
            p="sm"
            style={{ borderRadius: "var(--mantine-radius-md)", border: "1px solid var(--mantine-color-default-border)" }}
          >
            <Group gap="md" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" size={36} radius="md" style={{ flexShrink: 0 }}>
                <Icon size={20} />
              </ThemeIcon>
              <Stack gap={4} flex={1} miw={0}>
                <Text fw={600} fz="sm">
                  {g.title}
                </Text>
                {g.summary && (
                  <Text fz="xs" c="dimmed" lineClamp={2}>
                    {g.summary}
                  </Text>
                )}
                <GuideProgress done={g.done} total={g.total} status={g.status} />
              </Stack>
              <Box c="var(--mantine-primary-color-filled)" style={{ flexShrink: 0, alignSelf: "center" }}>
                <ChevronRight size={18} />
              </Box>
            </Group>
          </UnstyledButton>
        );
      })}
    </Stack>
  );
}
